import React, { useEffect, useRef, useState } from 'react'
import o from '../../components/Overlay.module.css'
import ov from './overlays.module.css'
import { apiJson } from '../../lib/api'
import { typeHm } from './time'
import type { Team } from './types'
import { useChanged, useLayer } from '../../components/layers'
import { PendingButton, useSlow, inertClass, Failed } from '../../components/pending'

// Bulk edit over the selected workers (CONTROL V4 bulkPanel / applyTsBulk).
// The plan (who changes, who is already there, who cannot be changed and why)
// is the server's: POST …/preview runs the same code the apply does, so the
// table shown is exactly what Apply writes.

export type BulkOp = 'start' | 'finish' | 'adjust' | 'breaks' | 'sessions'

export const BULK_BUTTONS: { label: string; op: BulkOp }[] = [
  { label: 'Set start', op: 'start' },
  { label: 'Set finish', op: 'finish' },
  { label: 'Adjust time', op: 'adjust' },
  { label: 'Breaks', op: 'breaks' },
  { label: 'Sessions by team', op: 'sessions' },
]

const ROUTE: Record<BulkOp, string> = {
  start: 'set-start',
  finish: 'set-finish',
  adjust: 'adjust-time',
  breaks: 'breaks',
  sessions: 'sessions-by-team',
}

const TITLES: Record<BulkOp, string> = {
  start: 'Set shift start',
  finish: 'Set shift finish',
  adjust: 'Adjust time',
  breaks: 'Set breaks',
  sessions: 'Edit sessions by team',
}

const NOTES: Record<BulkOp, string> = {
  start: 'Only each worker’s earliest session will change. Later sessions keep their times.',
  finish: 'Only each worker’s final session will change. Earlier sessions keep their times.',
  adjust: 'Moves one edge of the shift by the minutes you enter. Negative moves it earlier.',
  breaks:
    'Sets shift-level break overrides. Anything left blank stays as it is. A value that differs from the break engine’s own figures is marked manual; one that matches them stays computed.',
  sessions: 'Only sessions on the chosen team change. A worker with no matching session is left alone; a worker with two needs an explicit target.',
}

interface Vals {
  time: string
  mins: string
  edge: 'start' | 'finish'
  paid: string
  unpaid: string
  team: string
  which: 'ask' | 'first' | 'last'
  start: string
  finish: string
  reason: string
}

interface Plan {
  rows: { shift_id: string; worker_name: string; before: string | null; after: string | null; sessions: number }[]
  blocked: { shift_id: string; worker_name: string; why: string }[]
  same: { shift_id: string; worker_name: string; why: string }[]
}

interface Props {
  user: any
  op: BulkOp
  shiftIds: string[]
  teams: Team[]
  onClose: () => void
  onApplied: (flash: string) => void
}

const minsOnly = (v: string) => v.replace(/[−–]/g, '-').replace(/[^0-9-]/g, '').slice(0, 4)

function bodyFor(op: BulkOp, v: Vals, shiftIds: string[]) {
  const b: any = { shift_ids: shiftIds, reason: v.reason.trim() }
  if (op === 'start' || op === 'finish') b.time = v.time
  if (op === 'adjust') Object.assign(b, { edge: v.edge, minutes: v.mins })
  if (op === 'breaks') Object.assign(b, { paid_minutes: v.paid, unpaid_minutes: v.unpaid })
  if (op === 'sessions') Object.assign(b, { team_id: v.team, which: v.which, start_time: v.start, finish_time: v.finish })
  return b
}

export function BulkModal({ user, op, shiftIds, teams, onClose, onApplied }: Props) {
  const [v, setV] = useState<Vals>({
    time: '',
    mins: '',
    edge: 'finish',
    paid: '',
    unpaid: '',
    team: teams[0]?.id || '',
    which: 'ask',
    start: '',
    finish: '',
    reason: '',
  })
  const [plan, setPlan] = useState<Plan>({ rows: [], blocked: [], same: [] })
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const seq = useRef(0)
  const slow = useSlow(busy)
  // A layer (h 425-458): Escape, the scrim and Cancel ask first once a value
  // has changed. Nothing closes it while its write is out (h 3984-3992).
  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged(v)
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: dirty && !busy, el: panelEl, modal: true, focusFirst: true, busy })
  const set =<K extends keyof Vals>(k: K, val: Vals[K]) => setV((p) => ({ ...p, [k]: val }))

  // The reason does not change the plan, so it does not re-run the preview.
  const planKey = JSON.stringify({ ...v, reason: '' })
  useEffect(() => {
    const n = ++seq.current
    const t = setTimeout(async () => {
      try {
        const r = await apiJson<Plan>(user, `/api/timesheets/bulk/${ROUTE[op]}/preview`, { body: bodyFor(op, v, shiftIds) })
        if (n === seq.current) {
          setPlan({ rows: r.rows || [], blocked: r.blocked || [], same: r.same || [] })
          setError(null)
        }
      } catch (e: any) {
        if (n === seq.current) setError(e.message || 'Could not preview this change')
      }
    }, 200)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planKey, op, shiftIds.join(), user?.uid])

  async function apply() {
    if (!plan.rows.length) return
    setBusy(true)
    try {
      const r = await apiJson<any>(user, `/api/timesheets/bulk/${ROUTE[op]}`, { body: bodyFor(op, v, shiftIds) })
      onApplied([r.flash, r.note].filter(Boolean).join(' · '))
    } catch (e: any) {
      setError(e.message || 'Could not apply this change')
      setBusy(false)
    }
  }

  const n = plan.rows.length
  const timeField = (label: string, k: 'time' | 'start' | 'finish', placeholder: string) => (
    <div className={`${o.field} ${ov.narrowField}`}>
      <label className={o.fieldLabel} htmlFor={`bulk-${k}`}>{label}</label>
      <input id={`bulk-${k}`} className={o.input} value={v[k]} placeholder={placeholder} onChange={(e) => set(k, typeHm(e.target.value))} />
    </div>
  )
  const minsField = (label: string, k: 'mins' | 'paid' | 'unpaid', placeholder: string) => (
    <div className={`${o.field} ${ov.narrowField}`}>
      <label className={o.fieldLabel} htmlFor={`bulk-${k}`}>{label}</label>
      <input id={`bulk-${k}`} className={o.input} value={v[k]} placeholder={placeholder} onChange={(e) => set(k, minsOnly(e.target.value))} />
    </div>
  )

  return (
    <>
      <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
        <div ref={panelEl} tabIndex={-1} className={`${o.modal} ${ov.split} ${ov.bulkModal}`} role="dialog" aria-label={TITLES[op]}>
          <div className={ov.splitHead}>
            <div className={o.headText}>
              <h2 className={o.title}>{TITLES[op]}</h2>
              <p className={o.sub}>{NOTES[op]}</p>
            </div>
          </div>

          <div className={`${ov.splitBody} ${inertClass(busy, slow)}`}>
            {(op === 'start' || op === 'finish') && timeField(op === 'start' ? 'New start' : 'New finish', 'time', '14:30')}
            {op === 'adjust' && (
              <>
                <div className={`${o.field} ${ov.narrowField}`}>
                  <span className={o.fieldLabel}>Which edge</span>
                  <div className={ov.segRow}>
                    {(
                      [
                        ['start', 'First start'],
                        ['finish', 'Final finish'],
                      ] as const
                    ).map(([k, label]) => (
                      <button key={k} className={v.edge === k ? `${ov.seg} ${ov.segOn}` : ov.seg} aria-pressed={v.edge === k} onClick={() => set('edge', k)}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                {minsField('Minutes (− to move earlier)', 'mins', '−15')}
              </>
            )}
            {op === 'breaks' && (
              <>
                {minsField('Paid break (min)', 'paid', 'Leave unchanged')}
                {minsField('Unpaid break (min)', 'unpaid', 'Leave unchanged')}
              </>
            )}
            {op === 'sessions' && (
              <>
                <div className={`${o.field} ${ov.narrowField}`}>
                  <label className={o.fieldLabel} htmlFor="bulk-team">Team</label>
                  <select id="bulk-team" className={o.input} value={v.team} onChange={(e) => set('team', e.target.value)}>
                    {teams.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={`${o.field} ${ov.narrowField}`}>
                  <label className={o.fieldLabel} htmlFor="bulk-which">If a worker has two matching sessions</label>
                  <select id="bulk-which" className={o.input} value={v.which} onChange={(e) => set('which', e.target.value as Vals['which'])}>
                    <option value="ask">Ask me: leave them unchanged</option>
                    <option value="first">First matching session</option>
                    <option value="last">Last matching session</option>
                  </select>
                </div>
                {timeField('Start', 'start', 'Leave unchanged')}
                {timeField('Finish', 'finish', 'Leave unchanged')}
              </>
            )}

            <div>
              <div className={ov.planCount}>
                <strong>
                  {n} of {shiftIds.length} selected workers will change
                </strong>
                {plan.blocked.length > 0 && <span className={ov.badgeBad}>{plan.blocked.length} cannot be changed</span>}
                {plan.same.length > 0 && <span className={ov.badgeSame}>{plan.same.length} already at that value</span>}
              </div>
              <div className={ov.planTable} role="table" aria-label="Bulk change preview">
                <div className={ov.planHead} role="row">
                  <span>Worker</span>
                  <span>Before</span>
                  <span>After</span>
                </div>
                {plan.rows.map((r) => (
                  <div key={r.shift_id} className={ov.planRow} role="row">
                    <div>
                      <div className={ov.planWorker}>{r.worker_name}</div>
                      <div className={ov.planSub}>
                        {r.sessions} session{r.sessions === 1 ? '' : 's'}
                      </div>
                    </div>
                    <span className={ov.planBefore}>{r.before || '—'}</span>
                    <span className={ov.planAfter}>{r.after || '—'}</span>
                  </div>
                ))}
                {plan.same.map((r) => (
                  <div key={r.shift_id} className={ov.planSame}>
                    <strong>{r.worker_name}</strong> <span>{r.why}</span>
                  </div>
                ))}
                {plan.blocked.map((r) => (
                  <div key={r.shift_id} className={ov.planBlocked}>
                    <strong>{r.worker_name}</strong> <span>{r.why}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="bulk-reason">Reason: shared by every audit entry this creates. Optional.</label>
              <input id="bulk-reason" className={o.input} value={v.reason} placeholder="Crew finished early, rain" onChange={(e) => set('reason', e.target.value)} />
            </div>
          </div>

          <div className={ov.splitFoot}>
            <button className={o.ghost} onClick={requestClose} disabled={busy}>
              Cancel
            </button>
            <Failed text={error} />
            <PendingButton className={o.primary} onClick={apply} disabled={!n} busy={busy} pending="Applying…">
              {n ? `Apply to ${n} worker${n === 1 ? '' : 's'}` : 'Nothing to apply'}
            </PendingButton>
          </div>
        </div>
      </div>
      {confirm}
    </>
  )
}
